import { useEffect, useMemo, useRef } from "react";
import { Plot } from "@/types/plot";
import { EntryCollection } from "@/types/entryCollection";
import { EntryConfig } from "@/types/entryConfig";
import { extractImagesFromEntryCollection, getImageDownloadURL } from "@/utils/entryUtils";
import { preRenderAnnotatedThumbnail, isAnnotatedThumbnailCached } from "@/services/annotatedThumbnailCache";
import { getAnnotationClassNamesForEntryConfig, getTaskAnnotationClassColorOrder } from "@/utils/annotationClassConfig";
import { shouldRenderAnnotatedImage } from "@/utils/deferredAnalysisUtils";

/** Maximum number of images to preload per adjacent plot to avoid flooding the network */
const MAX_IMAGES_PER_PLOT = 5;

interface UseImagePreloaderParams {
  /** Adjacent plots to preload images for [prev, next, up, down] */
  adjacentPlots: (Plot | null)[];
  /** Function to get entry collections for a specific plot */
  getEntriesForPlot: (plot: Plot) => EntryCollection[];
  /** Entry configs for trait name mapping (optional, for better cache keys) */
  entryConfigs?: EntryConfig[];
  /** Only preload when enabled (e.g., when sheet is open and lightbox is closed) */
  enabled: boolean;
}

/**
 * Preloads images for adjacent plots to improve perceived navigation performance.
 *
 * When the PlotDetailsSheet is open, this hook:
 * 1. Extracts image paths from adjacent plots' entry collections
 * 2. Resolves Firebase Storage paths to signed download URLs
 * 3. Preloads images into browser cache using `new Image()`
 * 4. For annotated images: pre-fetches annotation metadata JSON
 *
 * Browser cache preloading works because:
 * - When we create `new Image()` and set its `src`, the browser fetches and caches the image
 * - When `StorageImage` later renders with the same URL, it's an instant cache hit
 */
export function useImagePreloader({
  adjacentPlots,
  getEntriesForPlot,
  entryConfigs,
  enabled,
}: UseImagePreloaderParams): void {
  // Track which URLs we've already started preloading to avoid duplicates
  const preloadedUrlsRef = useRef<Set<string>>(new Set());

  // Build config ID to name map for trait naming
  const configIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (entryConfigs) {
      entryConfigs.forEach((config) => {
        if (config.id) {
          map.set(config.id, config.name || config.id);
        }
      });
    }
    return map;
  }, [entryConfigs]);
  const configById = useMemo(
    () =>
      new Map(
        (entryConfigs ?? []).flatMap((entryConfig) => (entryConfig.id ? [[entryConfig.id, entryConfig] as const] : []))
      ),
    [entryConfigs]
  );
  const annotationClassColorOrder = useMemo(() => getTaskAnnotationClassColorOrder(entryConfigs ?? []), [entryConfigs]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Clear preloaded URLs when adjacent plots change
    // This prevents unbounded Set growth and allows re-preloading after re-uploads
    preloadedUrlsRef.current.clear();

    // Process each adjacent plot that exists
    const validPlots = adjacentPlots.filter((p): p is Plot => p !== null);

    validPlots.forEach((plot) => {
      // Get entry collections for this plot
      const plotCollections = getEntriesForPlot(plot);

      // Extract all images from these collections
      const images = plotCollections.flatMap((collection) => {
        const configId = collection.entryConfigRef?.id;
        const traitName = configId ? configIdToName.get(configId) : undefined;
        const annotationClassNames = getAnnotationClassNamesForEntryConfig(
          configId ? configById.get(configId) : undefined
        );
        return extractImagesFromEntryCollection(collection, traitName).map((image) => ({
          ...image,
          annotationClassNames,
        }));
      });

      // Limit to first N images to avoid flooding the network
      const imagesToPreload = images.slice(0, MAX_IMAGES_PER_PLOT);

      // Preload each image (fire-and-forget - no abort needed)
      imagesToPreload.forEach(async (image) => {
        // Skip if already preloading/preloaded this URL
        if (preloadedUrlsRef.current.has(image.url)) {
          return;
        }
        preloadedUrlsRef.current.add(image.url);

        try {
          // For annotated images, pre-render the full annotated thumbnail
          // This is the most important optimization since annotation rendering is slow
          if (shouldRenderAnnotatedImage(image)) {
            // Check if already cached
            if (
              !isAnnotatedThumbnailCached(
                image.url,
                image.captureMetadataCloudPath,
                image.updatedAt,
                image.annotationClassNames,
                annotationClassColorOrder
              )
            ) {
              await preRenderAnnotatedThumbnail(
                image.url,
                image.captureMetadataCloudPath,
                image.updatedAt,
                image.annotationClassNames,
                annotationClassColorOrder
              );
            }
          } else {
            // For non-annotated images, just preload the raw image
            const downloadUrl = await getImageDownloadURL(image.url, image.uploadStatus, image.updatedAt);
            if (downloadUrl) {
              const img = new Image();
              img.src = downloadUrl;
            }
          }
        } catch {
          // Silently fail - preloading is best-effort optimization
          // Network errors, missing files, etc. are all expected
        }
      });
    });

    // No cleanup needed - preloading is fire-and-forget
    // The browser will cache the images even if the component unmounts
  }, [adjacentPlots, annotationClassColorOrder, configById, configIdToName, enabled, getEntriesForPlot]);
}
